/**
 * Ticket 114.3 — Demo editor page.
 *
 * Opens after wizard completion (or from DemoListPage). Allows section-by-section
 * editing of a demo's content JSONB. Theme switching applies data-theme on a
 * preview pane for live preview.
 *
 * Permissions:
 *   demo.read           — view
 *   demo.refine.advanced — edit content sections
 *   demo.publish        — publish action
 *
 * Route: /demos/:demoId
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Link, Mail, MessageCircle, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { RequirePermission } from '@/components/RequirePermission';
import { apiClient } from '@/lib/apiClient';
import {
  getDemo,
  updateDemo,
  refineDemoSection,
  publishDemo,
  rotateShareToken,
  revokeShareToken,
  setDemoVisibility,
  handoffDemo,
  shareDemoViaEmail,
  shareDemoViaWhatsApp,
  type Demo,
  type DemoTheme,
  type DemoContent,
  type ContentSection,
} from '@/api/demos';

interface OpportunitySuggestion {
  id: string;
  name: string;
}

const THEMES: DemoTheme[] = ['sunburst', 'editorial', 'quantum', 'aurora'];

function parseContent(raw: string): DemoContent {
  try {
    const parsed = JSON.parse(raw) as DemoContent;
    return parsed;
  } catch {
    return { schemaVersion: 1, sections: {} };
  }
}

export function DemoEditorPage() {
  const { t } = useTranslation();
  const { demoId } = useParams<{ demoId: string }>();

  const [demo, setDemo] = useState<Demo | null>(null);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<DemoContent>({ schemaVersion: 1, sections: {} });
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null);
  const [previewTheme, setPreviewTheme] = useState<DemoTheme>('sunburst');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refining, setRefining] = useState(false);
  const refineInputRef = useRef<HTMLTextAreaElement>(null);

  // Phase 6 — opportunity picker
  const [opportunitySearch, setOpportunitySearch] = useState('');
  const [opportunitySuggestions, setOpportunitySuggestions] = useState<OpportunitySuggestion[]>([]);
  const [opportunityId, setOpportunityId] = useState<string | null>(null);

  // Phase 6 — share modals
  const [shareEmailOpen, setShareEmailOpen] = useState(false);
  const [shareWhatsappOpen, setShareWhatsappOpen] = useState(false);
  const [shareRecipients, setShareRecipients] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareContactId, setShareContactId] = useState('');
  const [sharing, setSharing] = useState(false);

  const load = useCallback(async () => {
    if (!demoId) return;
    setLoading(true);
    try {
      const d = await getDemo(demoId);
      setDemo(d);
      setContent(parseContent(d.content));
      setPreviewTheme(d.theme as DemoTheme);
      setOpportunityId(d.opportunityId);
      // Default to first section key
      const firstKey = Object.keys(parseContent(d.content).sections)[0] ?? null;
      setActiveSectionKey(firstKey);
    } catch {
      setDemo(null);
    } finally {
      setLoading(false);
    }
  }, [demoId]);

  const searchOpportunities = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setOpportunitySuggestions([]);
      return;
    }
    try {
      const { data } = await apiClient.get<OpportunitySuggestion[]>('/opportunities', {
        params: { search: q.trim(), limit: 5 },
      });
      setOpportunitySuggestions(data);
    } catch {
      setOpportunitySuggestions([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleSaveSection() {
    if (!demoId || !activeSectionKey) return;
    setSaving(true);
    try {
      const updated = await updateDemo(demoId, { content: JSON.stringify(content) });
      setDemo(updated);
      showToast(t('demos.editor.action.saveSection') + ' ✓');
    } catch {
      showToast(t('demos.editor.error.save'));
    } finally {
      setSaving(false);
    }
  }

  async function handleMarkApproved() {
    if (!activeSectionKey) return;
    setContent((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [activeSectionKey]: {
          ...prev.sections[activeSectionKey],
          status: 'approved' as const,
        },
      },
    }));
    await handleSaveSection();
  }

  async function handleRequestRefinement() {
    if (!demoId) return;
    setSaving(true);
    try {
      await handoffDemo(demoId);
      const updated = await getDemo(demoId);
      setDemo(updated);
      showToast(t('demos.handoff.success'));
    } catch {
      showToast(t('demos.handoff.error'));
    } finally {
      setSaving(false);
    }
  }

  async function handleShareViaEmail() {
    if (!demoId) return;
    const emails = shareRecipients
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (emails.length === 0) return;
    setSharing(true);
    try {
      await shareDemoViaEmail(demoId, { recipientEmails: emails, message: shareMessage || null });
      setShareEmailOpen(false);
      setShareRecipients('');
      setShareMessage('');
      showToast(t('demos.share.success'));
    } catch {
      showToast(t('demos.share.error'));
    } finally {
      setSharing(false);
    }
  }

  async function handleShareViaWhatsApp() {
    if (!demoId || !shareContactId.trim()) return;
    setSharing(true);
    try {
      await shareDemoViaWhatsApp(demoId, {
        opportunityContactId: shareContactId.trim(),
        message: shareMessage || null,
      });
      setShareWhatsappOpen(false);
      setShareContactId('');
      setShareMessage('');
      showToast(t('demos.share.success'));
    } catch {
      showToast(t('demos.share.error'));
    } finally {
      setSharing(false);
    }
  }

  async function handleLinkOpportunity(id: string, name: string) {
    if (!demoId) return;
    try {
      const updated = await updateDemo(demoId, { opportunityId: id });
      setDemo(updated);
      setOpportunityId(id);
      setOpportunitySearch(name);
      setOpportunitySuggestions([]);
    } catch {
      showToast(t('demos.opportunityPicker.label') + ' — error');
    }
  }

  async function handlePublish() {
    if (!demoId) return;
    setPublishing(true);
    try {
      const result = await publishDemo(demoId);
      setPublicUrl(result.publicUrl);
      // Reload demo to reflect PUBLISHED status
      const updated = await getDemo(demoId);
      setDemo(updated);
      showToast(t('demos.editor.publish.success'));
    } catch {
      showToast(t('demos.editor.publish.failed'));
    } finally {
      setPublishing(false);
    }
  }

  async function handleRotateShareToken() {
    if (!demoId) return;
    setSharingLoading(true);
    try {
      const result = await rotateShareToken(demoId);
      setShareUrl(result.shareableUrl);
      const updated = await getDemo(demoId);
      setDemo(updated);
      showToast(t('demos.editor.share.rotate') + ' ✓');
    } catch {
      showToast(t('demos.editor.publish.failed'));
    } finally {
      setSharingLoading(false);
    }
  }

  async function handleRevokeShareToken() {
    if (!demoId) return;
    setSharingLoading(true);
    try {
      await revokeShareToken(demoId);
      setShareUrl(null);
      const updated = await getDemo(demoId);
      setDemo(updated);
      showToast(t('demos.editor.share.revoke') + ' ✓');
    } catch {
      showToast(t('demos.editor.publish.failed'));
    } finally {
      setSharingLoading(false);
    }
  }

  async function handleTogglePublic() {
    if (!demoId || !demo) return;
    try {
      const updated = await setDemoVisibility(demoId, !demo.isPublic);
      setDemo(updated);
    } catch {
      showToast(t('demos.editor.publish.failed'));
    }
  }

  function handleCopyUrl(url: string) {
    void navigator.clipboard.writeText(url);
    showToast(t('demos.editor.publish.copyUrl') + ' ✓');
  }

  async function handleThemeSwitch(th: DemoTheme) {
    setPreviewTheme(th);
    if (!demoId) return;
    try {
      const updated = await updateDemo(demoId, { theme: th });
      setDemo(updated);
    } catch {
      // Non-critical — preview still updates locally
    }
  }

  async function handleRefineSection() {
    if (!demoId || !activeSectionKey || !refineInstruction.trim()) return;
    setRefining(true);
    try {
      const updatedSection = await refineDemoSection(demoId, activeSectionKey, refineInstruction.trim());
      // Merge the updated section into local content state
      setContent((prev) => ({
        ...prev,
        sections: {
          ...prev.sections,
          [activeSectionKey]: updatedSection as ContentSection,
        },
      }));
      setRefineInstruction('');
      showToast(t('demos.editor.action.saveSection') + ' ✓');
    } catch {
      showToast(t('demos.editor.refine.failed'));
    } finally {
      setRefining(false);
    }
  }

  if (loading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }

  if (!demo) {
    return <div className="p-8 text-muted-foreground text-sm">{t('demos.editor.notFound')}</div>;
  }

  const sectionKeys = Object.keys(content.sections);
  const activeSection: ContentSection | null = activeSectionKey
    ? (content.sections[activeSectionKey] ?? null)
    : null;

  return (
    <div className="flex flex-col h-screen">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-background border border-border rounded-md px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      {/* Sticky top bar */}
      <div className="border-b border-border bg-background px-6 py-3 sticky top-0 z-10 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <h1 className="text-base font-semibold truncate">{demo.businessName}</h1>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            {demo.industryKey} · {demo.country}
          </span>
        </div>
        {/* Theme switcher */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t('demos.editor.preview.label')}</span>
          <div className="flex gap-1">
            {THEMES.map((th) => (
              <button
                key={th}
                type="button"
                onClick={() => handleThemeSwitch(th)}
                aria-pressed={previewTheme === th}
                className={`text-xs px-2 py-0.5 rounded border transition-colors ${previewTheme === th ? 'border-[color:var(--color-lop-teal)] text-[color:var(--color-lop-teal)]' : 'border-border text-muted-foreground'}`}
              >
                {t(`demos.theme.${th}.label`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left rail — section list */}
        <div className="w-56 border-r border-border bg-background overflow-y-auto shrink-0 py-4">
          <p className="px-3 text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
            {t('demos.editor.sections')}
          </p>
          {sectionKeys.length === 0 ? (
            <p className="px-3 text-xs text-muted-foreground">{t('demos.editor.noSections')}</p>
          ) : (
            <ul>
              {sectionKeys.map((key) => {
                const sec = content.sections[key];
                const isActive = key === activeSectionKey;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => setActiveSectionKey(key)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition-colors
                        ${isActive ? 'bg-[color:var(--color-lop-teal)]/10 text-[color:var(--color-lop-teal)]' : 'hover:bg-muted/30 text-foreground'}`}
                    >
                      <span className="truncate capitalize">{key}</span>
                      {sec?.status === 'approved' && (
                        <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                      )}
                      {sec?.status === 'empty' && (
                        <span className="h-2 w-2 rounded-full bg-muted-foreground/30 shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Main editor */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeSection ? (
            <div className="max-w-2xl space-y-4">
              <h2 className="text-base font-semibold capitalize">
                {t(`demos.editor.section.${activeSectionKey}.title`, {
                  defaultValue: activeSectionKey ?? '',
                })}
              </h2>

              <div className="space-y-3">
                {/* Headline */}
                {activeSection.headline !== undefined && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground" htmlFor="ed-headline">
                      {t('demos.editor.section.hero.headline')}
                    </label>
                    <input
                      id="ed-headline"
                      type="text"
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                      value={activeSection.headline}
                      onChange={(e) =>
                        setContent((prev) => ({
                          ...prev,
                          sections: {
                            ...prev.sections,
                            [activeSectionKey!]: {
                              ...prev.sections[activeSectionKey!],
                              headline: e.target.value,
                              status: 'needs-edit',
                            },
                          },
                        }))
                      }
                    />
                  </div>
                )}

                {/* Body / copy */}
                {activeSection.body !== undefined && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground" htmlFor="ed-body">
                      {t('demos.editor.section.body')}
                    </label>
                    <textarea
                      id="ed-body"
                      rows={6}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm resize-none"
                      value={activeSection.body}
                      onChange={(e) =>
                        setContent((prev) => ({
                          ...prev,
                          sections: {
                            ...prev.sections,
                            [activeSectionKey!]: {
                              ...prev.sections[activeSectionKey!],
                              body: e.target.value,
                              status: 'needs-edit',
                            },
                          },
                        }))
                      }
                    />
                  </div>
                )}

                {/* Image URL */}
                {activeSection.imageUrl !== undefined && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground" htmlFor="ed-img">
                      {t('demos.editor.section.imageUrl')}
                    </label>
                    <input
                      id="ed-img"
                      type="url"
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                      value={activeSection.imageUrl ?? ''}
                      onChange={(e) =>
                        setContent((prev) => ({
                          ...prev,
                          sections: {
                            ...prev.sections,
                            [activeSectionKey!]: {
                              ...prev.sections[activeSectionKey!],
                              imageUrl: e.target.value,
                              status: 'needs-edit',
                            },
                          },
                        }))
                      }
                    />
                  </div>
                )}
              </div>

              {/* Section actions */}
              <div className="flex gap-2 flex-wrap pt-2">
                <RequirePermission permission="demo.refine.advanced">
                  <Button onClick={handleSaveSection} disabled={saving}>
                    {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : t('demos.editor.action.saveSection')}
                  </Button>
                  <Button variant="secondary" onClick={handleMarkApproved} disabled={saving}>
                    {t('demos.editor.action.markApproved')}
                  </Button>
                </RequirePermission>
              </div>

              {/* Refine with AI — requires demo.refine.advanced */}
              <RequirePermission permission="demo.refine.advanced">
                <div className="border border-border rounded-md p-3 space-y-2 mt-2 bg-muted/10">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Sparkles className="h-3.5 w-3.5" />
                    {t('demos.editor.refine.button')}
                  </div>
                  <textarea
                    ref={refineInputRef}
                    rows={2}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm resize-none"
                    placeholder={t('demos.editor.refine.placeholder')}
                    value={refineInstruction}
                    onChange={(e) => setRefineInstruction(e.target.value)}
                    aria-label={t('demos.editor.refine.placeholder')}
                  />
                  <Button
                    size="sm"
                    onClick={handleRefineSection}
                    disabled={refining || !refineInstruction.trim()}
                  >
                    {refining
                      ? t('demos.editor.refine.loading')
                      : t('demos.editor.refine.button')}
                  </Button>
                </div>
              </RequirePermission>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">{t('demos.editor.noSectionSelected')}</p>
          )}
        </div>

        {/* Preview pane */}
        <div
          className="w-80 border-l border-border bg-background overflow-y-auto shrink-0 p-4 hidden lg:block"
          data-theme={previewTheme}
        >
          <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
            {t('demos.editor.preview.label')}
          </p>
          <div className="rounded-lg border border-border bg-muted/20 p-4 text-xs space-y-2">
            <div className="font-semibold">{demo.businessName}</div>
            <div className="text-muted-foreground">{demo.industryKey}</div>
            <div className="text-muted-foreground">{t(`demos.theme.${previewTheme}.label`)}</div>
            <div className="text-muted-foreground capitalize">{demo.status}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border bg-background px-6 py-3 space-y-3">
        {/* Opportunity picker */}
        <div className="relative">
          <label className="text-xs text-muted-foreground block mb-1">
            {t('demos.opportunityPicker.label')}
          </label>
          <input
            type="text"
            data-testid="opportunity-picker-input"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            placeholder={t('demos.opportunityPicker.placeholder')}
            value={opportunitySearch}
            onChange={(e) => {
              setOpportunitySearch(e.target.value);
              void searchOpportunities(e.target.value);
            }}
          />
          {opportunitySuggestions.length > 0 && (
            <ul className="absolute bottom-full mb-1 w-full rounded-md border border-border bg-background shadow-lg z-20 text-sm">
              {opportunitySuggestions.map((opp) => (
                <li key={opp.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-muted/40 transition-colors"
                    onClick={() => { void handleLinkOpportunity(opp.id, opp.name); }}
                  >
                    {opp.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {opportunityId && !opportunitySearch && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('demos.opportunityPicker.none')} — {opportunityId}
            </p>
          )}
        </div>

        <div className="flex justify-between items-center flex-wrap gap-2">
          <RequirePermission permission="demo.create.basic">
            <Button
              variant="secondary"
              onClick={() => { void handleRequestRefinement(); }}
              disabled={saving || demo.status === 'AWAITING_REFINEMENT'}
            >
              {t('demos.handoff.cta')}
            </Button>
          </RequirePermission>

          <RequirePermission permission="demo.create.basic">
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setShareEmailOpen(true); setShareMessage(''); setShareRecipients(''); }}
              >
                <Mail className="h-4 w-4 mr-1.5" />
                {t('demos.share.email')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setShareWhatsappOpen(true); setShareMessage(''); setShareContactId(''); }}
              >
                <MessageCircle className="h-4 w-4 mr-1.5" />
                {t('demos.share.whatsapp')}
              </Button>
            </div>
          </RequirePermission>

          <RequirePermission permission="demo.publish">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Visibility toggle */}
              <button
                type="button"
                onClick={() => { void handleTogglePublic(); }}
                className="flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded px-2 py-1 hover:bg-muted/30 transition-colors"
                title={demo.isPublic ? t('demos.editor.visibility.public') : t('demos.editor.visibility.private')}
              >
                {demo.isPublic ? t('demos.editor.visibility.public') : t('demos.editor.visibility.private')}
              </button>

              {/* Publish button */}
              <Button
                onClick={() => { void handlePublish(); }}
                disabled={publishing || demo.status === 'ARCHIVED'}
              >
                {publishing ? <RefreshCw className="h-4 w-4 animate-spin" /> : t('demos.editor.publish.button')}
              </Button>
            </div>
          </RequirePermission>
        </div>

        {/* Public URL display (after publish) */}
        {publicUrl && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link className="h-3 w-3 shrink-0" />
            <span className="truncate">{publicUrl}</span>
            <button
              type="button"
              onClick={() => handleCopyUrl(publicUrl)}
              className="shrink-0 hover:text-foreground transition-colors"
              aria-label={t('demos.editor.publish.copyUrl')}
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Share link panel */}
        <RequirePermission permission="demo.publish">
          <div className="border border-border rounded-md p-3 space-y-2 bg-muted/10">
            <p className="text-xs font-medium text-muted-foreground">{t('demos.editor.share.title')}</p>
            {shareUrl || demo.shareToken ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground truncate flex-1">
                  {shareUrl ?? `${publicUrl ?? ''}?token=${demo.shareToken ?? ''}`}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopyUrl(shareUrl ?? `?token=${demo.shareToken ?? ''}`)}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={t('demos.editor.share.copy')}
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{t('demos.editor.share.urlLabel')}</p>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => { void handleRotateShareToken(); }} disabled={sharingLoading}>
                {sharingLoading ? <RefreshCw className="h-3 w-3 animate-spin" /> : t('demos.editor.share.rotate')}
              </Button>
              {(demo.shareToken || shareUrl) && (
                <Button size="sm" variant="secondary" onClick={() => { void handleRevokeShareToken(); }} disabled={sharingLoading}>
                  {t('demos.editor.share.revoke')}
                </Button>
              )}
            </div>
          </div>
        </RequirePermission>
      </div>

      {/* Share via Email modal */}
      {shareEmailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-lg border border-border bg-background p-6 space-y-4 shadow-xl">
            <h3 className="text-sm font-semibold">{t('demos.share.modal.titleEmail')}</h3>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="share-email-recipients">
                {t('demos.share.recipients')}
              </label>
              <input
                id="share-email-recipients"
                type="text"
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                placeholder={t('demos.share.recipientsPlaceholder')}
                value={shareRecipients}
                onChange={(e) => setShareRecipients(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="share-email-message">
                {t('demos.share.message')}
              </label>
              <textarea
                id="share-email-message"
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm resize-none"
                placeholder={t('demos.share.messagePlaceholder')}
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setShareEmailOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={() => { void handleShareViaEmail(); }} disabled={sharing || !shareRecipients.trim()}>
                {sharing ? <RefreshCw className="h-3 w-3 animate-spin" /> : t('demos.share.send')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Share via WhatsApp modal */}
      {shareWhatsappOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-lg border border-border bg-background p-6 space-y-4 shadow-xl">
            <h3 className="text-sm font-semibold">{t('demos.share.modal.titleWhatsapp')}</h3>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="share-wa-contact">
                {t('demos.share.recipients')}
              </label>
              <input
                id="share-wa-contact"
                type="text"
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                placeholder="Contact ID"
                value={shareContactId}
                onChange={(e) => setShareContactId(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="share-wa-message">
                {t('demos.share.message')}
              </label>
              <textarea
                id="share-wa-message"
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm resize-none"
                placeholder={t('demos.share.messagePlaceholder')}
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setShareWhatsappOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={() => { void handleShareViaWhatsApp(); }} disabled={sharing || !shareContactId.trim()}>
                {sharing ? <RefreshCw className="h-3 w-3 animate-spin" /> : t('demos.share.send')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
